import { FormEvent, useEffect, useState } from "react";
import { CheckCircle2, Circle, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  AdminLogisticsCarrier,
  AdminLogisticsCarrierPayload,
  LogisticsMode,
  LogisticsSetting,
  deleteAdminLogisticsCarrier,
  getCurrentAdmin,
  getLogisticsSetting,
  listAdminLogisticsCarriers,
  logout,
  saveAdminLogisticsCarrier,
  updateLogisticsSetting,
} from "./api";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";

type FormState = {
  Mode: LogisticsMode;
  MerchantId: string;
  HashKey: string;
  HashIV: string;
  SenderName: string;
  SenderPhone: string;
  SenderCellPhone: string;
  SenderZipCode: string;
  SenderAddress: string;
  ServerReplyUrl: string;
  ClientReplyUrl: string;
  IsEnabled: boolean;
  IsStoreMapEnabled: boolean;
};

const emptyForm: FormState = {
  Mode: "Test",
  MerchantId: "",
  HashKey: "",
  HashIV: "",
  SenderName: "",
  SenderPhone: "",
  SenderCellPhone: "",
  SenderZipCode: "",
  SenderAddress: "",
  ServerReplyUrl: "",
  ClientReplyUrl: "",
  IsEnabled: false,
  IsStoreMapEnabled: false,
};

const emptyCarrierForm: AdminLogisticsCarrierPayload = {
  Code: "",
  Name: "",
  ContactName: "",
  ContactPhone: "",
  TrackingUrlTemplate: "",
  Note: "",
  IsActive: true,
  SortOrder: 100,
};

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const secretFields = [
  { key: "MerchantId" as const, label: "MerchantId" },
  { key: "HashKey" as const, label: "HashKey" },
  { key: "HashIV" as const, label: "HashIV" },
];

export default function AdminLogisticsSettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [setting, setSetting] = useState<LogisticsSetting | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [carriers, setCarriers] = useState<AdminLogisticsCarrier[]>([]);
  const [carrierFormOpen, setCarrierFormOpen] = useState(false);
  const [carrierEditingId, setCarrierEditingId] = useState<string | null>(null);
  const [carrierForm, setCarrierForm] = useState<AdminLogisticsCarrierPayload>(emptyCarrierForm);
  const [carrierSaving, setCarrierSaving] = useState(false);

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getLogisticsSetting();
      setSetting(data.Item);
      setCarriers(data.Carriers || []);
      setForm({
        ...emptyForm,
        Mode: data.Item.Mode || "Test",
        SenderName: data.Item.SenderName || "",
        SenderPhone: data.Item.SenderPhone || "",
        SenderCellPhone: data.Item.SenderCellPhone || "",
        SenderZipCode: data.Item.SenderZipCode || "",
        SenderAddress: data.Item.SenderAddress || "",
        ServerReplyUrl: data.Item.ServerReplyUrl || emptyForm.ServerReplyUrl,
        ClientReplyUrl: data.Item.ClientReplyUrl || emptyForm.ClientReplyUrl,
        IsEnabled: data.Item.IsEnabled,
        IsStoreMapEnabled: data.Item.IsStoreMapEnabled,
      });
      listAdminLogisticsCarriers()
        .then((carrierData) => setCarriers(carrierData.Items || []))
        .catch(() => undefined);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "物流設定讀取失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  function openCreateCarrier() {
    setCarrierEditingId(null);
    setCarrierForm(emptyCarrierForm);
    setCarrierFormOpen(true);
  }

  function openEditCarrier(item: AdminLogisticsCarrier) {
    setCarrierEditingId(item.LogisticsCarrierId);
    setCarrierForm({
      Code: item.Code || "",
      Name: item.Name || "",
      ContactName: item.ContactName || "",
      ContactPhone: item.ContactPhone || "",
      TrackingUrlTemplate: item.TrackingUrlTemplate || "",
      Note: item.Note || "",
      IsActive: item.IsActive,
      SortOrder: item.SortOrder,
    });
    setCarrierFormOpen(true);
  }

  async function submitCarrier(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCarrierSaving(true);
    setMessage("");
    try {
      const result = await saveAdminLogisticsCarrier(carrierEditingId, {
        ...carrierForm,
        Code: carrierForm.Code.trim(),
        Name: carrierForm.Name.trim(),
        ContactName: carrierForm.ContactName?.trim() || "",
        ContactPhone: carrierForm.ContactPhone?.trim() || "",
        TrackingUrlTemplate: carrierForm.TrackingUrlTemplate?.trim() || "",
        Note: carrierForm.Note?.trim() || "",
      });
      const carrierData = await listAdminLogisticsCarriers();
      setCarriers(carrierData.Items || []);
      setCarrierFormOpen(false);
      setCarrierEditingId(null);
      setCarrierForm(emptyCarrierForm);
      setMessage(result.message || "物流公司已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "物流公司儲存失敗。");
    } finally {
      setCarrierSaving(false);
    }
  }

  async function removeCarrier(item: AdminLogisticsCarrier) {
    if (!window.confirm(`確定要刪除「${item.Name}」嗎？`)) return;
    setCarrierSaving(true);
    setMessage("");
    try {
      const result = await deleteAdminLogisticsCarrier(item.LogisticsCarrierId);
      const carrierData = await listAdminLogisticsCarriers();
      setCarriers(carrierData.Items || []);
      setMessage(result.message || "物流公司已刪除。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "物流公司刪除失敗。");
    } finally {
      setCarrierSaving(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const saved = await updateLogisticsSetting({
        Provider: "ECPay",
        Mode: form.Mode,
        Config: {
          MerchantId: form.MerchantId.trim(),
          HashKey: form.HashKey.trim(),
          HashIV: form.HashIV.trim(),
        },
        SenderName: form.SenderName.trim(),
        SenderPhone: form.SenderPhone.trim(),
        SenderCellPhone: form.SenderCellPhone.trim(),
        SenderZipCode: form.SenderZipCode.trim(),
        SenderAddress: form.SenderAddress.trim(),
        ServerReplyUrl: form.ServerReplyUrl.trim(),
        ClientReplyUrl: form.ClientReplyUrl.trim(),
        IsEnabled: form.IsEnabled,
        IsStoreMapEnabled: form.IsStoreMapEnabled,
      });
      setSetting(saved.Item);
      setForm((current) => ({ ...current, MerchantId: "", HashKey: "", HashIV: "" }));
      setMessage(saved.message || "物流設定已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "物流設定儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  const configStatus = setting?.ConfigStatus || { MerchantId: false, HashKey: false, HashIV: false };

  return (
    <AdminLayout sectionLabel="網站設定" title="物流串接設定" user={currentUser} onLogout={handleLogout}>
      <div className="space-y-5">
      <form onSubmit={handleSubmit}>
        {message && <div className="mb-4 rounded-md border border-[#d8e0db] bg-white px-4 py-3 text-sm font-medium text-[#315746]">{message}</div>}

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">讀取中...</div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">綠界 ECPay</h2>
                <AdminRadioCardGroup<LogisticsMode>
                  name="Mode"
                  value={form.Mode}
                  onChange={(Mode) => setField("Mode", Mode)}
                  options={[
                    { value: "Test", label: "測試環境", description: "用綠界測試參數驗證流程。" },
                    { value: "Production", label: "正式環境", description: "確認綠界審核與正式參數後再啟用。" },
                  ]}
                  columnsClassName="sm:grid-cols-2"
                />
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">API 參數</h2>
                <div className="grid gap-4">
                  {secretFields.map((field) => (
                    <label className="grid gap-2 text-sm font-bold text-[#315746]" key={field.key}>
                      <span className="flex items-center gap-1.5">
                        {configStatus[field.key] ? <CheckCircle2 className="text-[#2f8f5b]" size={14} /> : <Circle className="text-[#c3cdc7]" size={14} />}
                        {field.label}
                      </span>
                      <input className={inputClass} placeholder={configStatus[field.key] ? "已設定，留空不覆蓋" : `請輸入 ${field.label}`} value={form[field.key]} onChange={(event) => setField(field.key, event.target.value)} />
                    </label>
                  ))}
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">寄件人資料</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>寄件人姓名</span>
                    <input className={inputClass} value={form.SenderName} onChange={(event) => setField("SenderName", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>寄件電話</span>
                    <input className={inputClass} value={form.SenderPhone} onChange={(event) => setField("SenderPhone", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>寄件手機</span>
                    <input className={inputClass} value={form.SenderCellPhone} onChange={(event) => setField("SenderCellPhone", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>郵遞區號</span>
                    <input className={inputClass} value={form.SenderZipCode} onChange={(event) => setField("SenderZipCode", event.target.value)} />
                  </label>
                </div>
                <label className="mt-4 grid gap-2 text-sm font-bold text-[#315746]">
                  <span>寄件地址</span>
                  <input className={inputClass} value={form.SenderAddress} onChange={(event) => setField("SenderAddress", event.target.value)} />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">回呼網址</h2>
                <div className="grid gap-4">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>ServerReplyURL</span>
                    <input className={inputClass} value={form.ServerReplyUrl} onChange={(event) => setField("ServerReplyUrl", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>ClientReplyURL</span>
                    <input className={inputClass} value={form.ClientReplyUrl} onChange={(event) => setField("ClientReplyUrl", event.target.value)} />
                  </label>
                </div>
              </section>
            </div>

            <aside className="min-w-0 space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4 lg:sticky lg:top-20 lg:z-10">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">狀態</h2>
                <div className="mb-4 space-y-1 text-xs text-[#7b877f]">
                  <p>參數：{setting?.IsConfigured ? "已設定" : "未完整"}</p>
                  <p>啟用：{setting?.IsReady ? "可用" : "未啟用或未完整"}</p>
                  {setting?.UpdatedAt && <p>更新：{setting.UpdatedAt}</p>}
                </div>
                <div className="mb-4 grid gap-3">
                  <AdminSwitchCard checked={form.IsStoreMapEnabled} description="正式電子地圖完成前可先關閉，保留人工必填門市資料。" label="啟用電子地圖" onChange={(IsStoreMapEnabled) => setField("IsStoreMapEnabled", IsStoreMapEnabled)} />
                  <AdminSwitchCard checked={form.IsEnabled} description="啟用前請確認綠界參數與寄件人資料完整。" label="啟用物流串接" onChange={(IsEnabled) => setField("IsEnabled", IsEnabled)} />
                </div>
                <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                  <Save size={18} />
                  {saving ? "儲存中" : "儲存設定"}
                </button>
              </section>
            </aside>
          </div>
        )}
      </form>
      {!loading ? (
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#203d32]">物流公司主檔</h2>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">ERP 出庫、物流單號與簽收追蹤會優先使用這裡啟用的物流公司；若尚未設定，ERP 端才允許手動輸入。</p>
            </div>
            <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateCarrier} type="button">
              <Plus size={18} />
              新增物流公司
            </button>
          </div>

          {carrierFormOpen ? (
            <form className="mt-4 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" onSubmit={submitCarrier}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-extrabold text-[#203d32]">{carrierEditingId ? "編輯物流公司" : "新增物流公司"}</h3>
                <span className="rounded-full bg-[#eaf4ef] px-3 py-1 text-xs font-extrabold text-[#315746]">ERP 可選主檔</span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>物流代碼</span>
                  <input className={inputClass} placeholder="例如 BLACKCAT、HCT" value={carrierForm.Code} onChange={(event) => setCarrierForm((current) => ({ ...current, Code: event.target.value.toUpperCase() }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>物流公司名稱</span>
                  <input className={inputClass} placeholder="例如 黑貓宅急便" value={carrierForm.Name} onChange={(event) => setCarrierForm((current) => ({ ...current, Name: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>聯絡窗口</span>
                  <input className={inputClass} value={carrierForm.ContactName || ""} onChange={(event) => setCarrierForm((current) => ({ ...current, ContactName: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>聯絡電話</span>
                  <input className={inputClass} value={carrierForm.ContactPhone || ""} onChange={(event) => setCarrierForm((current) => ({ ...current, ContactPhone: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746] md:col-span-2 xl:col-span-3">
                  <span>物流追蹤網址</span>
                  <input className={inputClass} placeholder="請包含 {TrackingNumber}" value={carrierForm.TrackingUrlTemplate || ""} onChange={(event) => setCarrierForm((current) => ({ ...current, TrackingUrlTemplate: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>排序</span>
                  <input className={inputClass} min={0} type="number" value={carrierForm.SortOrder} onChange={(event) => setCarrierForm((current) => ({ ...current, SortOrder: Number(event.target.value || 0) }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746] md:col-span-2 xl:col-span-3">
                  <span>備註</span>
                  <input className={inputClass} value={carrierForm.Note || ""} onChange={(event) => setCarrierForm((current) => ({ ...current, Note: event.target.value }))} />
                </label>
                <label className="flex min-h-11 items-center gap-3 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]">
                  <input className="h-5 w-5 accent-[#315746]" checked={carrierForm.IsActive} type="checkbox" onChange={(event) => setCarrierForm((current) => ({ ...current, IsActive: event.target.checked }))} />
                  啟用物流公司
                </label>
              </div>
              <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={carrierSaving} onClick={() => { setCarrierFormOpen(false); setCarrierEditingId(null); setCarrierForm(emptyCarrierForm); }} type="button">取消</button>
                <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={carrierSaving} type="submit">{carrierSaving ? "儲存中" : "儲存物流公司"}</button>
              </div>
            </form>
          ) : null}

          <div className="mt-4 overflow-x-auto rounded-md border border-[#edf0ee]">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="px-4 py-3">代碼</th>
                  <th className="px-4 py-3">物流公司</th>
                  <th className="px-4 py-3">聯絡資訊</th>
                  <th className="px-4 py-3">追蹤網址</th>
                  <th className="px-4 py-3">狀態</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {carriers.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center font-semibold text-[#7b877f]" colSpan={6}>尚未建立物流公司，ERP 端會保留手動輸入。</td></tr>
                ) : carriers.map((item) => (
                  <tr key={item.LogisticsCarrierId}>
                    <td className="px-4 py-3 font-extrabold text-[#203d32]">{item.Code}</td>
                    <td className="px-4 py-3"><p className="font-extrabold text-[#203d32]">{item.Name}</p>{item.Note ? <p className="mt-1 text-xs font-semibold text-[#7b877f]">{item.Note}</p> : null}</td>
                    <td className="px-4 py-3 font-semibold text-[#59665f]">{item.ContactName || item.ContactPhone ? `${item.ContactName || "-"} / ${item.ContactPhone || "-"}` : "-"}</td>
                    <td className="max-w-[360px] break-all px-4 py-3 text-xs font-semibold text-[#59665f]">{item.TrackingUrlTemplate || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-extrabold ${item.IsActive ? "bg-[#eaf4ef] text-[#315746]" : "bg-[#f1f3f2] text-[#7b877f]"}`}>{item.IsActive ? "啟用" : "停用"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => openEditCarrier(item)} type="button"><Pencil size={16} />編輯</button>
                        <button className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#f1c9c9] bg-white px-4 font-bold text-[#a33a3a]" onClick={() => removeCarrier(item)} type="button"><Trash2 size={16} />刪除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      </div>
    </AdminLayout>
  );
}
